import React, { useState, useEffect, useRef, useCallback } from 'react';

// Orphan Mode — a two-pane connect canvas inside the graph.
//   • Orphan lane: untraced items of `orphanLevel` (lazy-scrolled from its orphan filter).
//   • Parent pane: items at the adjacent level (searchable across the whole panel).
// Tick orphans → click a parent → confirm → link. The relation is always recorded
// deeper → shallower (source = higher level, target = lower), whichever the parent is.

const PAGE = 25;
const near = (el) => el.scrollHeight - el.scrollTop - el.clientHeight < 140;

export default function OrphanMode({
  orphanLevel, levels,
  onFetchOrphans, onFetchParents, onCreateRelation, onReconcile, onExit,
}) {
  const levelInfo = (n) => (levels || []).find((l) => Number(l.level_num) === Number(n)) || {};
  const parentLevel = orphanLevel === 1 ? 2 : orphanLevel - 1; // L1 orphans connect down to L2
  const dockLeft = orphanLevel === 1;                          // orphan lane sits left for L1
  const oInfo = levelInfo(orphanLevel);
  const pInfo = levelInfo(parentLevel);

  // ---- orphan lane state ----
  const [orphans, setOrphans] = useState([]);
  const [oTotal, setOTotal] = useState(0);
  const [oHasMore, setOHasMore] = useState(false);
  const [oLoading, setOLoading] = useState(false);
  const [oError, setOError] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  // ---- parent pane state ----
  const [parents, setParents] = useState([]);
  const [pTotal, setPTotal] = useState(0);
  const [pHasMore, setPHasMore] = useState(false);
  const [pLoading, setPLoading] = useState(false);
  const [pError, setPError] = useState('');
  const [search, setSearch] = useState('');

  const [confirm, setConfirm] = useState(null);  // { parentKey }
  const [linking, setLinking] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const footRef = useRef(null);

  const showToast = (msg, kind = 'ok') => {
    setToast({ msg, kind });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3600);
  };
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const loadOrphans = useCallback(async (offset) => {
    setOLoading(true); setOError('');
    const r = await onFetchOrphans(orphanLevel, offset, PAGE);
    setOLoading(false);
    if (!r.ok) { setOError(r.error || 'Failed to load orphans'); return; }
    setOTotal(r.total || 0); setOHasMore(!!r.hasMore);
    setOrphans((prev) => (offset === 0 ? (r.orphans || []) : [...prev, ...(r.orphans || [])]));
  }, [onFetchOrphans, orphanLevel]);

  const loadParents = useCallback(async (offset, term) => {
    setPLoading(true); setPError('');
    const r = await onFetchParents(parentLevel, term, offset, PAGE);
    setPLoading(false);
    if (!r.ok) { setPError(r.error || 'Failed to load parents'); return; }
    setPTotal(r.total || 0); setPHasMore(!!r.hasMore);
    setParents((prev) => (offset === 0 ? (r.items || []) : [...prev, ...(r.items || [])]));
  }, [onFetchParents, parentLevel]);

  // (Re)load both panes when the orphan level changes
  useEffect(() => {
    setSelected(new Set()); setSearch(''); setOrphans([]); setParents([]);
    loadOrphans(0); loadParents(0, '');
  }, [orphanLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced parent search (across the whole panel, not just the current page)
  useEffect(() => {
    const t = setTimeout(() => loadParents(0, search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const onOScroll = (e) => { if (oHasMore && !oLoading && near(e.currentTarget)) loadOrphans(orphans.length); };
  const onPScroll = (e) => { if (pHasMore && !pLoading && near(e.currentTarget)) loadParents(parents.length, search.trim()); };

  const toggle = (key) => setSelected((prev) => {
    const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n;
  });

  const flashFoot = () => {
    const el = footRef.current; if (!el) return;
    el.classList.remove('flash'); void el.offsetWidth; el.classList.add('flash');
  };

  const onParentClick = (parentKey) => {
    if (selected.size === 0) { flashFoot(); return; }
    setConfirm({ parentKey });
  };

  // deeper -> shallower resolution for a single orphan/parent pair
  const buildAttempt = (orphanKey, parentKey) => (
    orphanLevel > parentLevel
      ? { sourceKey: orphanKey, targetKey: parentKey, sourceLevel: orphanLevel, targetLevel: parentLevel }
      : { sourceKey: parentKey, targetKey: orphanKey, sourceLevel: parentLevel, targetLevel: orphanLevel }
  );

  const doCreate = async () => {
    if (!confirm) return;
    const parentKey = confirm.parentKey;
    const keys = [...selected];
    setLinking(true);
    const results = await Promise.all(keys.map((k) => onCreateRelation(buildAttempt(k, parentKey))));
    setLinking(false);
    setConfirm(null);

    const okKeys = keys.filter((k, i) => results[i] && results[i].ok);
    const failed = keys.length - okKeys.length;
    // Clear the whole attempted batch from the selection — a failed one shouldn't stay
    // checked. Successful ones also leave the list (they're no longer orphans); a failed
    // one stays in the list, just unchecked, so it can be re-selected and retried.
    setSelected((prev) => { const n = new Set(prev); keys.forEach((k) => n.delete(k)); return n; });
    if (okKeys.length) {
      setOrphans((prev) => prev.filter((o) => !okKeys.includes(o.key)));
      setOTotal((t) => Math.max(0, t - okKeys.length));
      if (onReconcile) onReconcile();                                       // rebuild the graph in the background
    }
    if (failed === 0) showToast(`Linked ${okKeys.length} item${okKeys.length > 1 ? 's' : ''} to ${parentKey}`, 'ok');
    else if (okKeys.length) showToast(`Linked ${okKeys.length}, ${failed} failed — still selected`, 'warn');
    else {
      const err = results[0] && results[0].error;
      showToast(`Couldn't link to ${parentKey}${err ? ': ' + err : ''}`, 'warn');
    }
  };

  const armed = selected.size > 0;

  const parentPane = (
    <div className="om-pane om-parent">
      <div className="om-pane-head">
        <span className="om-pane-title">L{parentLevel} {pInfo.name || ''} · <span className="n">{pTotal}</span></span>
        <input
          className="om-search"
          placeholder="Search parents by key or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="om-scroll" onScroll={onPScroll}>
        {pError && <div className="om-error">{pError}</div>}
        {parents.map((p) => (
          <div
            key={p.key}
            className={`om-parent-card${armed ? ' armed' : ''}`}
            onClick={() => onParentClick(p.key)}
            title={armed ? `Link ${selected.size} selected here` : 'Select orphans first, then click a parent'}
          >
            <span className="om-dot" />
            <div className="om-meta">
              <div className="om-key">{p.key}</div>
              <div className="om-name">{p.name}</div>
            </div>
            <span className="om-arm">＋</span>
          </div>
        ))}
        {pLoading && <div className="om-lazy"><span className="om-spin" />loading…</div>}
        {!pLoading && parents.length === 0 && !pError && (
          <div className="om-empty">{search ? 'No matching parents' : 'No items at this level'}</div>
        )}
      </div>
    </div>
  );

  const orphanPane = (
    <div className="om-pane om-orphan">
      <div className="om-pane-head">
        <span className="om-pane-title om-orphan-title">L{orphanLevel} orphans · <span className="n">{oTotal}</span></span>
      </div>
      <div className="om-scroll" onScroll={onOScroll}>
        {oError && <div className="om-error">{oError}</div>}
        {!oLoading && orphans.length === 0 && !oError && (
          <div className="om-empty">No orphan items — everything at this level is traced 🎉</div>
        )}
        {orphans.map((o) => {
          const sel = selected.has(o.key);
          return (
            <div key={o.key} className={`om-card${sel ? ' sel' : ''}`} onClick={() => toggle(o.key)}>
              <span className="om-box">{sel ? '✓' : ''}</span>
              <div className="om-meta">
                <div className="om-key">{o.key}</div>
                <div className="om-name">{o.name}</div>
                <span className="om-tag">untraced</span>
              </div>
            </div>
          );
        })}
        {oLoading && <div className="om-lazy"><span className="om-spin" />loading…</div>}
      </div>
      <div ref={footRef} className={`om-foot${armed ? ' ready' : ''}`}>
        <span>{armed ? `${selected.size} selected — click a parent to link` : 'Select items, then click a parent'}</span>
        <span className="om-pill">{selected.size}</span>
      </div>
    </div>
  );

  return (
    <div className="om">
      <div className="om-head">
        <button type="button" className="om-back" onClick={onExit}>← Back to graph</button>
        <div className="om-headmeta">
          <div className="om-title">Orphan mode · <b>L{orphanLevel} {oInfo.name || ''}</b></div>
          <div className="om-sub">
            Tick untraced items, then click a parent (<b>L{parentLevel} {pInfo.name || ''}</b>) to link them.
            Recorded deeper → shallower.
          </div>
        </div>
      </div>

      <div className={`om-body${dockLeft ? ' dock-left' : ''}`}>
        {parentPane}
        {orphanPane}
      </div>

      {confirm && (
        <div className="graph-confirm-wrap" onClick={() => !linking && setConfirm(null)}>
          <div className="graph-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="gc-title">{selected.size > 1 ? `Link ${selected.size} items?` : 'Create trace?'}</div>
            <div className="gc-body">
              <div className="gc-stack">
                {[...selected].map((k) => <span key={k} className="gc-key">{k}</span>)}
              </div>
              <span className="gc-arrow">→</span>
              <span className="gc-key gc-parent">{confirm.parentKey}</span>
            </div>
            <div className="gc-note">Each trace is recorded deeper → shallower in Orcanos.</div>
            <div className="gc-actions">
              <button type="button" className="gc-btn gc-cancel" disabled={linking} onClick={() => setConfirm(null)}>Cancel</button>
              <button type="button" className="gc-btn gc-create" disabled={linking} onClick={doCreate}>
                {linking ? 'Linking…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`graph-toast ${toast.kind}`} role="status">
          <span className="gt-ic">{toast.kind === 'warn' ? '⚠' : toast.kind === 'info' ? 'ℹ' : '✓'}</span>
          <span className="gt-msg">{toast.msg}</span>
          <button type="button" className="gt-close" onClick={() => setToast(null)} aria-label="Dismiss">×</button>
        </div>
      )}
    </div>
  );
}
